/**
 * How long is left on a Cue cooldown, broken into the units a sentence needs.
 *
 * Deliberately free of imports — no i18n, no API client — so the arithmetic can
 * be tested on its own. `cue-cooldown.ts` turns these parts into a localized
 * string; nothing here knows what language anything is in.
 *
 * The rule: **two units, never rounded down.** A single rounded unit cannot be
 * both honest and readable on a continuous countdown — 1h05m becomes "2 hours"
 * rounded up, which asks for an hour that isn't owed, or "1 hour" rounded down,
 * which invites a retry that fails again. Naming the remainder avoids the
 * choice. The exact durations, which is what a fresh refusal almost always
 * carries, still come out clean: "1 hour", "48 hours".
 *
 * **Hours are the largest unit. There is no day.** This file used to carry one,
 * and the longest cooldown — 48 hours — therefore rendered as "2 days", which
 * is the same sentence whether 48 hours remain or 25: it sounds like a sentence
 * passed rather than a clock running, at exactly the moment the sender wants to
 * know how long the wait actually is. The API's `cue-cooldown-refusal.ts` had
 * already settled this and says so in its own comment; only this file was left
 * behind, so the server's fallback sentence said "48 hours" while the localized
 * one the user actually saw said "2 days".
 *
 * Nothing routed here can exceed 48 hours, so dropping the unit cannot produce
 * an absurd figure: the ladder is 1h then 48h for a repeat, and 12h after an
 * expiry. A block is permanent and hides the person outright rather than
 * refusing with a countdown.
 */

export type CountdownUnit = "underMinute" | "minutes" | "hours";

export type CountdownPart = { unit: CountdownUnit; count: number };

/** One part, or two when a remainder is worth naming. Never empty. */
export function countdownParts(seconds: number): CountdownPart[] {
  if (seconds < 60) return [{ unit: "underMinute", count: 0 }];

  if (seconds < 3600) {
    return [{ unit: "minutes", count: Math.ceil(seconds / 60) }];
  }

  // No upper branch: an hour is the largest unit, so 48 hours reads as
  // "48 hours" rather than collapsing into "2 days".
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.ceil((seconds - hours * 3600) / 60);
  // A remainder that fills its own unit carries, rather than reading
  // "1 hour 60 minutes".
  if (minutes >= 60) return [{ unit: "hours", count: hours + 1 }];
  if (minutes === 0) return [{ unit: "hours", count: hours }];
  return [
    { unit: "hours", count: hours },
    { unit: "minutes", count: minutes },
  ];
}

/**
 * Seconds left from now. Floored at 1: a brake that lifts between the response
 * and this call is still a refusal, and "0 seconds" is not an answer.
 */
export function secondsUntil(retryAt: string, now: Date = new Date()): number | null {
  const at = Date.parse(retryAt);
  if (Number.isNaN(at)) return null;
  return Math.max(1, Math.ceil((at - now.getTime()) / 1000));
}
